import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { RefreshCcw, Sparkles, Type } from "lucide-react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

function PromoPreviewCard({ form }) {
  const [showDetails, setShowDetails] = useState(false);

  const isNavCash = form.promo_code_type === "NAVCASH";
  const isUpi = form.promo_code_type === "UPI";
  const isPercent = form.discount_type === "PERCENT";
  const isCashbackPercent = form.cashback_type === "PERCENT";

  // ── Left banner text ─────────────────────────────────────────
  const leftText = (() => {
    if (isUpi) {
      const max = Number(form.max_booking_discount_amount) || 0;
      return `Save up to ₹${max} with UPI`;
    }
    if (isNavCash) {
      const val = Number(form.cashback_value) || 0;
      if (isCashbackPercent) {
        const max = Number(form.max_booking_discount_amount) || 0;
        return max > 0 ? `Upto ₹${max} NavCash` : `${val}% NavCash`;
      }
      return `Earn ₹${val} NavCash`;
    }
    // Normal / Targeted
    if (!isPercent) return `Flat ₹${form.discount_value || 0} off`;
    const max = Number(form.max_booking_discount_amount) || 0;
    if (max > 0) return `Upto ₹${max} off`;
    return `Upto ${form.discount_value || 0}% off`;
  })();

  // ── Sub-headline below code ──────────────────────────────────
  const previewText =
    form.short_description?.trim()
      ? form.short_description.trim()
      : isUpi
      ? `Pay with UPI and save up to ₹${form.max_booking_discount_amount || 0} on bookings above ₹${form.min_booking_amount || 0}.`
      : isNavCash
      ? (() => {
          const val = Number(form.cashback_value) || 0;
          if (isCashbackPercent) {
            const max = Number(form.max_booking_discount_amount) || 0;
            return max > 0
              ? `Earn up to ₹${max} NavCash on bookings above ₹${form.min_booking_amount || 0}.`
              : `Earn ${val}% NavCash on bookings above ₹${form.min_booking_amount || 0}.`;
          }
          return `Earn ₹${val} NavCash on bookings above ₹${form.min_booking_amount || 0}.`;
        })()
      : isPercent
      ? `Save up to ₹${form.max_booking_discount_amount || 0} on all transactions above ₹${form.min_booking_amount || 0}.`
      : `Save ₹${form.discount_value || 0} on all transactions above ₹${form.min_booking_amount || 0}.`;

  // ── Badge below preview text ─────────────────────────────────
  const badgeText = (() => {
    if (isUpi) {
      return form.min_booking_amount ? `Min booking ₹${form.min_booking_amount} · UPI only` : "UPI only";
    }
    if (isNavCash) {
      if (isCashbackPercent) {
        return form.max_booking_discount_amount ? `Max NavCash ₹${form.max_booking_discount_amount}` : null;
      }
      return null;
    }
    if (isPercent && form.max_booking_discount_amount)
      return `Max discount ₹${form.max_booking_discount_amount}`;
    return null;
  })();

  return (
    <div className="w-full border rounded-2xl overflow-hidden shadow-sm bg-white">
      <div className="flex">
        <div className="bg-gray-800 text-white flex items-center justify-center px-3">
          <span className="rotate-[-90deg] text-lg font-bold whitespace-nowrap">
            {leftText}
          </span>
        </div>

        <div className="flex-1 p-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-wide">
                {(form.code || "PROMOCODE").toUpperCase()}
              </h2>
              <p className="text-gray-600 mt-2 text-sm">{previewText}</p>
              {badgeText ? (
                <p className="text-xs text-gray-500 mt-1">{badgeText}</p>
              ) : null}
            </div>
            <img src="/logo.png" alt="logo" className="h-10 w-auto object-contain" />
          </div>

          <div className="flex items-center gap-6 mt-4">
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="text-blue-600 font-semibold text-sm underline underline-offset-4"
            >
              View Details
            </button>
            <button
              type="button"
              disabled={true}
              className="ml-auto rounded-full bg-blue-600 px-6 py-2 text-white text-sm font-semibold opacity-50 cursor-not-allowed"
            >
              Apply Code
            </button>
          </div>
        </div>
      </div>

      {showDetails && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Promo Details</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-sm px-3 py-1 rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
            <div className="mt-4 text-sm text-gray-700 whitespace-pre-wrap">
              {form.description || "No description added."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_FORM = {
  promo_code_trip_type: "FLIGHT",
  promo_code_type: "NORMAL",
  promo_wording_type: "NORMAL",
  origin_display_name: "",
  destination_display_name: "",
  code: "",
  discount_type: "PERCENT",
  discount_value: "",
  cashback_type: "PERCENT",
  cashback_value: "",
  trip_type: "ONE_WAY",
  passenger_type: "SINGLE",
  max_usage_per_user: 1,
  max_usage_per_device: 1,
  airlines: "",
  sectors: "",
  promo_country_code: "",
  country_display_name: "",
  start_date: "",
  end_date: "",
  travel_start_date: "",
  travel_end_date: "",
  status: 1,
  to_destination: "",
  applicable_start_sector: 0,
  applicable_end_sector: 0,
  min_booking_amount: "",
  description: "",
  short_description: "",
  max_booking_discount_amount: "",
  // Bus-specific
  bus_type: "ALL",
  min_passengers: "",
  max_passengers: "",
};

const safeParse = (v) => {
  try {
    if (v == null) return null;
    if (Array.isArray(v) || typeof v === "object") return v;
    if (typeof v === "string") {
      const s = v.trim();
      if (!s) return null;
      if (
        (s.startsWith("[") && s.endsWith("]")) ||
        (s.startsWith("{") && s.endsWith("}"))
      ) {
        return JSON.parse(s);
      }
      return v;
    }
    return null;
  } catch {
    return v;
  }
};

const toInputDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const extractAirportCode = (text) => {
  if (!text) return "";
  let t = String(text).trim().toUpperCase();
  t = t.replace(/^"+|"+$/g, "");
  const match = t.match(/\(([A-Z0-9]{3,4})\)/);
  if (match?.[1]) return match[1];
  if (/^[A-Z0-9]{3,4}$/.test(t)) return t;
  return "";
};

const extractDestinationLabel = (query, fallbackCode = "") => {
  const q = String(query || "").trim();
  if (!q) return fallbackCode || "";
  const match = q.match(/^(.+?)\s*\(/);
  if (match?.[1]) return match[1].trim();
  return q;
};

const extractOriginLabel = (query, fallbackCode = "") => {
  const q = String(query || "").trim();
  if (!q) return fallbackCode || "";
  const match = q.match(/^(.+?)\s*\(/);
  if (match?.[1]) return match[1].trim();
  return q;
};

const wordingTextsMatch = (a, b) => {
  const n = (v) => String(v ?? "").trim();
  return n(a?.short_description) === n(b?.short_description) && n(a?.description) === n(b?.description);
};

const to01 = (v) => {
  if (Array.isArray(v) || typeof v === "object") return 0;
  if (v === 1 || v === "1" || v === true) return 1;
  return 0;
};

// ── Airline tag-input with IATA-first autocomplete ───────────────────────
function AirlineTagInput({ value, onChange }) {
  // value: comma-separated IATA codes string, e.g. "AI,6E,SG"
  // onChange: (newCommaString) => void

  const [airlines, setAirlines] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useCallback((_node) => {}, []);

  // Load airlines.json once
  useEffect(() => {
    fetch("/airlines.json")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          // Only keep entries with a real IATA code
          setAirlines(data.filter((a) => a.iata && a.iata !== "-" && a.iata.trim()));
        }
      })
      .catch(() => {});
  }, []);

  // Derive current tags from the value prop
  const tags = useMemo(
    () =>
      (value || "")
        .split(",")
        .map((x) => x.trim().toUpperCase())
        .filter(Boolean),
    [value]
  );

  // Filter suggestions: IATA exact/prefix first, then name match
  useEffect(() => {
    const q = query.trim().toUpperCase();
    if (!q || !open) { setResults([]); return; }
    const iataExact = [];
    const iataPrefix = [];
    const nameMatch = [];
    for (const a of airlines) {
      if (tags.includes(a.iata.toUpperCase())) continue; // already added
      const iata = (a.iata || "").toUpperCase();
      const name = (a.name || "").toUpperCase();
      if (iata === q) iataExact.push(a);
      else if (iata.startsWith(q)) iataPrefix.push(a);
      else if (name.includes(q.toLowerCase() === q ? q : q)) nameMatch.push(a);
    }
    // name search with original case-insensitive query
    const nameLower = query.trim().toLowerCase();
    const nameMatchFinal = nameLower.length >= 2
      ? airlines.filter(
          (a) =>
            !tags.includes(a.iata.toUpperCase()) &&
            a.iata.toUpperCase() !== q &&
            !a.iata.toUpperCase().startsWith(q) &&
            (a.name || "").toLowerCase().includes(nameLower)
        )
      : [];
    setResults([...iataExact, ...iataPrefix, ...nameMatchFinal].slice(0, 10));
  }, [query, airlines, tags, open]);

  const addTag = (iata) => {
    const code = iata.toUpperCase();
    if (!tags.includes(code)) {
      onChange([...tags, code].join(","));
    }
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const removeTag = (iata) => {
    onChange(tags.filter((t) => t !== iata).join(","));
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && query.trim()) {
      e.preventDefault();
      // Accept if matches a known IATA or just raw code
      const q = query.trim().toUpperCase().replace(/,$/, "");
      const match = airlines.find((a) => a.iata.toUpperCase() === q);
      if (match) addTag(match.iata);
      else if (/^[A-Z0-9]{2,3}$/.test(q)) addTag(q); // allow raw code entry
    }
    if (e.key === "Backspace" && !query && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div>
      <label className="text-sm font-semibold">Airlines</label>
      <div className="relative mt-2">
        {/* Tag container + input */}
        <div
          className="min-h-[42px] w-full border rounded-xl px-3 py-2 flex flex-wrap gap-1.5 cursor-text focus-within:ring-2 focus-within:ring-black"
          onClick={() => { setOpen(true); }}
        >
          {tags.map((iata) => {
            const info = airlines.find((a) => a.iata.toUpperCase() === iata);
            return (
              <span
                key={iata}
                className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-800"
              >
                {iata}
                {info ? <span className="font-normal text-gray-500 truncate max-w-[100px]">{info.name}</span> : null}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeTag(iata); }}
                  className="ml-0.5 text-gray-400 hover:text-red-500 leading-none"
                  tabIndex={-1}
                >
                  ×
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
            value={query}
            placeholder={tags.length === 0 ? "Type IATA code or airline name…" : ""}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Dropdown */}
        {open && results.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
            {results.map((a) => (
              <button
                key={a.iata}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(a.iata)}
              >
                <span className="font-bold text-gray-900 w-8 shrink-0">{a.iata}</span>
                <span className="text-gray-600 truncate">{a.name}</span>
                {a.country ? <span className="ml-auto text-xs text-gray-400 shrink-0">{a.country}</span> : null}
              </button>
            ))}
          </div>
        )}
      </div>
      {tags.length > 0 && (
        <p className="text-xs text-gray-400 mt-1">Stored: {tags.join(", ")}</p>
      )}
    </div>
  );
}

// ── Bus city autocomplete component ─────────────────────────────────────
function BusCitySearch({ label, value, onSelect, disabled }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    if (!open || !query.trim() || query.trim().length < 1) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/admin/bus-cities/search", { params: { q: query.trim() } });
        setResults(res.data?.data || []);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  return (
    <div className="relative">
      <label className="text-xs font-medium">{label}</label>
      <input
        disabled={disabled}
        className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
        value={query}
        placeholder="Type city name..."
        onFocus={() => !disabled && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
          {results.map((c) => (
            <button
              key={c.city_id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setQuery(c.city_name);
                setOpen(false);
                onSelect(c.city_name);
              }}
            >
              {c.city_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Country-lock search: single box, resolves to one country, confirmed via chip ──
function CountrySearch({ code, displayName, onSelect, onClear, disabled }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/admin/promo-codes/countries/search", {
          params: { q: query.trim() },
        });
        setResults(res.data?.data || []);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  if (code) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 text-sm font-medium">
          {displayName || code} (all airports)
        </span>
        {!disabled && (
          <button
            type="button"
            className="text-xs text-gray-500 underline"
            onClick={onClear}
          >
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        disabled={disabled}
        className="w-full border rounded-xl px-3 py-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
        value={query}
        placeholder="Search country name, country code, or airport code..."
        onFocus={() => !disabled && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
          {results.map((c) => (
            <button
              key={c.country_code}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setQuery("");
                setOpen(false);
                onSelect(c.country_code, c.country_name);
              }}
            >
              {c.country_name} ({c.country_code})
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PromoUpsertPage() {
  const navigate = useNavigate();
  const { promoId } = useParams();
  const isEdit = Boolean(promoId);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [genLoading, setGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoWording, setAutoWording] = useState(null);
  const [wordingPreviewLoading, setWordingPreviewLoading] = useState(false);
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [wordingEditReason, setWordingEditReason] = useState("");
  const [wordingCustomized, setWordingCustomized] = useState(false);
  const wordingRequestIdRef = useRef(0);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);

  // Flight-specific state
  const [airports, setAirports] = useState([]);
  const [destQuery, setDestQuery] = useState("");
  const [destResults, setDestResults] = useState([]);
  const [destOpen, setDestOpen] = useState(false);
  const [sectorFromQuery, setSectorFromQuery] = useState("");
  const [sectorFromResults, setSectorFromResults] = useState([]);
  const [sectorFromOpen, setSectorFromOpen] = useState(false);

  // Bus-specific state: from/to city names stored directly in form.sectors (JSON pairs) and form.to_destination
  const [busOriginCity, setBusOriginCity] = useState("");
  const [busDestCity, setBusDestCity] = useState("");

  const isBus = form.promo_code_trip_type === "BUS";
  const isNavCash = form.promo_code_type === "NAVCASH";
  const isUpi = form.promo_code_type === "UPI";
  const isNormalFlight =
    form.promo_code_type === "NORMAL" && form.promo_code_trip_type === "FLIGHT";
  const isNavCashFlight = isNavCash && form.promo_code_trip_type === "FLIGHT";
  const isUpiFlight = isUpi && form.promo_code_trip_type === "FLIGHT";
  const hasCountryLock = Boolean(form.promo_country_code);
  const isCountryFlight = hasCountryLock && (isNormalFlight || isNavCashFlight || isUpiFlight);
  const isWordingEligible = isNormalFlight || isNavCashFlight || isUpiFlight;

  const destinationLabel = isBus
    ? busDestCity || form.to_destination
    : extractDestinationLabel(destQuery, form.to_destination);

  const originLabel = isBus
    ? busOriginCity || form.sectors
    : extractOriginLabel(sectorFromQuery, form.sectors);

  useEffect(() => {
    if (isBus) return;
    const loadAirports = async () => {
      try {
        const res = await fetch("/airports.json");
        const json = await res.json();
        if (Array.isArray(json)) setAirports(json);
      } catch (e) {
        console.error("Failed to load airports.json", e);
      }
    };
    loadAirports();
  }, [isBus]);

  useEffect(() => {
    if (!isEdit) return;

    const loadPromo = async () => {
      try {
        const res = await api.patch(`/admin/promo-codes/${promoId}`);
        const initialData = res.data?.data;

        const airlinesParsed = safeParse(initialData.airlines);
        const sectorsParsed = safeParse(initialData.sectors);

        const airlinesText = Array.isArray(airlinesParsed)
          ? airlinesParsed.join(",")
          : typeof airlinesParsed === "string"
          ? airlinesParsed
          : "";

        const firstSector = Array.isArray(sectorsParsed) ? sectorsParsed[0] : null;
        const sectorValue = firstSector
          ? (typeof firstSector === "object" ? (firstSector.from || "") : String(firstSector))
          : typeof sectorsParsed === "string"
          ? sectorsParsed
          : "";

        const sectorsText = Array.isArray(sectorsParsed)
          ? sectorsParsed
              .map((s) => {
                const from = (s?.from || "").toUpperCase();
                const to = (s?.to || "").toUpperCase();
                return from && to ? `${from}-${to}` : "";
              })
              .filter(Boolean)
              .join(",")
          : typeof sectorsParsed === "string"
          ? sectorsParsed
          : "";

        const tripType = initialData.promo_code_trip_type || "FLIGHT";

        setForm({
          ...DEFAULT_FORM,
          promo_code_trip_type: tripType,
          promo_code_type: initialData.promo_code_type || "NORMAL",
          code: (initialData.code || "").toUpperCase(),
          discount_type: initialData.discount_type || "PERCENT",
          discount_value: initialData.discount_value ?? "",
          cashback_type: initialData.cashback_type || "PERCENT",
          cashback_value: initialData.cashback_value ?? "",
          trip_type: initialData.trip_type || "ONE_WAY",
          passenger_type: initialData.passenger_type || "SINGLE",
          max_usage_per_user: initialData.max_usage_per_user ?? 1,
          max_usage_per_device: initialData.max_usage_per_device ?? 1,
          airlines: airlinesText,
          sectors: sectorValue,
          start_date: toInputDate(initialData.start_date),
          end_date: toInputDate(initialData.end_date),
          travel_start_date: toInputDate(initialData.travel_start_date),
          travel_end_date: toInputDate(initialData.travel_end_date),
          status: Number(initialData.status ?? 1),
          to_destination: initialData.to_destination || "",
          promo_country_code: initialData.promo_country_code || "",
          country_display_name: initialData.country_display_name || "",
          applicable_start_sector: to01(initialData.applicable_start_sector),
          applicable_end_sector: to01(initialData.applicable_end_sector),
          min_booking_amount: initialData.min_booking_amount ?? "",
          description: initialData.description ?? "",
          short_description: initialData.short_description ?? "",
          max_booking_discount_amount: Number(initialData.max_booking_discount_amount) > 0 ? initialData.max_booking_discount_amount : "",
          bus_type: initialData.bus_type || "ALL",
          min_passengers: initialData.min_passengers ?? "",
          max_passengers: initialData.max_passengers ?? "",
          promo_wording_type: initialData.promo_wording_type || "NORMAL",
          origin_display_name: initialData.origin_display_name || "",
          destination_display_name: initialData.destination_display_name || "",
        });
        setWordingCustomized(false);

        if (tripType === "BUS") {
          // For bus, sectors are city name pairs
          if (Array.isArray(sectorsParsed) && sectorsParsed.length > 0 && sectorsParsed[0]?.from) {
            setBusOriginCity(sectorsParsed[0].from || "");
            setBusDestCity(sectorsParsed[0].to || "");
          } else {
            setBusOriginCity(sectorValue || "");
            setBusDestCity(initialData.to_destination || "");
          }
        } else {
          setDestQuery(initialData.to_destination || "");
          setSectorFromQuery(sectorValue || "");
        }
      } catch (e) {
        toast.error("Failed to load promo");
      }
    };

    loadPromo();
  }, [promoId, isEdit]);

  // Score airport for sorting — higher score = shown first
  // Priority 1: exact airport_code match in India
  // Priority 2: city_name match in India
  // Priority 3: city_name match outside India
  // Priority 4: exact airport_code match outside India
  const getAirportScore = (a, q) => {
    const code     = (a.airport_code  || "").toLowerCase();
    const city     = (a.city_name     || "").toLowerCase();
    // support both country_code:"IN" and country_name:"India"
    const isIndia  = (a.country_code  || "").toUpperCase() === "IN" ||
                     (a.country_name  || "").toLowerCase() === "india";

    if (isIndia && code === q)          return 7;  // exact code, India
    if (isIndia && city === q)          return 6;  // exact city, India
    if (isIndia && city.startsWith(q))  return 5;  // city starts, India
    if (isIndia && city.includes(q))    return 4;  // city contains, India
    if (city === q)                     return 3;  // exact city, other
    if (city.startsWith(q))             return 2;  // city starts, other
    if (city.includes(q))               return 1;  // city contains, other
    if (code === q)                     return 0;  // exact code, other
    return -1;
  };

  // Flight airport search effects
  useEffect(() => {
    if (isBus || !destOpen || !destQuery.trim()) { setDestResults([]); return; }
    const q = destQuery.trim().toLowerCase();
    setDestResults(
      airports
        .filter((a) => {
          const name = (a.airport_name || "").toLowerCase();
          const code = (a.airport_code || "").toLowerCase();
          const city = (a.city_name    || "").toLowerCase();
          return name.includes(q) || code.includes(q) || city.includes(q);
        })
        .sort((a, b) => getAirportScore(b, q) - getAirportScore(a, q))
        .slice(0, 10)
    );
  }, [destQuery, airports, destOpen, isBus]);

  useEffect(() => {
    if (isBus || !sectorFromOpen || !sectorFromQuery.trim()) { setSectorFromResults([]); return; }
    const q = sectorFromQuery.trim().toLowerCase();
    setSectorFromResults(
      airports
        .filter((a) => {
          const name = (a.airport_name || "").toLowerCase();
          const code = (a.airport_code || "").toLowerCase();
          const city = (a.city_name    || "").toLowerCase();
          return name.includes(q) || code.includes(q) || city.includes(q);
        })
        .sort((a, b) => getAirportScore(b, q) - getAirportScore(a, q))
        .slice(0, 10)
    );
  }, [sectorFromQuery, airports, sectorFromOpen, isBus]);

  useEffect(() => {
    if (!isWordingEligible) {
      wordingRequestIdRef.current += 1;
      setAutoWording(null);
      return undefined;
    }

    // Guard against out-of-order responses: if the user changes the form again
    // before this request resolves (e.g. toggling the country lock on/off
    // quickly), a slower/stale response must not overwrite a newer one.
    const requestId = ++wordingRequestIdRef.current;

    const timer = setTimeout(async () => {
      setWordingPreviewLoading(true);
      try {
        let res;
        if (isCountryFlight) {
          res = await api.post("/admin/promo-codes/preview-country-wording", {
            promo_code_type: form.promo_code_type,
            promo_code_trip_type: "FLIGHT",
            discount_type: form.discount_type,
            discount_value: Number(form.discount_value) || 0,
            cashback_type: form.cashback_type,
            cashback_value: Number(form.cashback_value) || 0,
            trip_type: form.trip_type,
            passenger_type: form.passenger_type,
            promo_wording_type: form.promo_wording_type,
            code: (form.code || "PREVIEW").toUpperCase(),
            min_booking_amount: form.min_booking_amount || 0,
            max_booking_discount_amount: form.max_booking_discount_amount || 0,
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            travel_start_date: form.travel_start_date || null,
            travel_end_date: form.travel_end_date || null,
            promo_country_code: form.promo_country_code,
            country_display_name: form.country_display_name,
          });
        } else if (isNormalFlight) {
          res = await api.post("/admin/promo-codes/preview-wording", {
            promo_code_type: "NORMAL",
            promo_code_trip_type: "FLIGHT",
            discount_type: form.discount_type,
            discount_value: Number(form.discount_value) || 0,
            trip_type: form.trip_type,
            passenger_type: form.passenger_type,
            promo_wording_type: form.promo_wording_type,
            code: (form.code || "PREVIEW").toUpperCase(),
            min_booking_amount: form.min_booking_amount || 0,
            max_booking_discount_amount: form.max_booking_discount_amount || 0,
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            travel_start_date: form.travel_start_date || null,
            travel_end_date: form.travel_end_date || null,
            to_destination: form.to_destination,
            sectors: form.sectors ? [String(form.sectors).trim().toUpperCase()] : null,
            airlines: (form.airlines || "")
              .split(",")
              .map((x) => x.trim().toUpperCase())
              .filter(Boolean),
            applicable_start_sector: to01(form.applicable_start_sector),
            applicable_end_sector: to01(form.applicable_end_sector),
            origin_display_name: originLabel,
            destination_display_name: destinationLabel,
          });
        } else if (isNavCashFlight) {
          res = await api.post("/admin/promo-codes/preview-navcash-wording", {
            promo_code_type: "NAVCASH",
            promo_code_trip_type: "FLIGHT",
            cashback_type: form.cashback_type,
            cashback_value: Number(form.cashback_value) || 0,
            max_booking_discount_amount: form.max_booking_discount_amount || 0,
            trip_type: form.trip_type,
            passenger_type: form.passenger_type,
            promo_wording_type: form.promo_wording_type,
            code: (form.code || "PREVIEW").toUpperCase(),
            min_booking_amount: form.min_booking_amount || 0,
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            travel_start_date: form.travel_start_date || null,
            travel_end_date: form.travel_end_date || null,
            to_destination: form.to_destination,
            sectors: form.sectors ? [String(form.sectors).trim().toUpperCase()] : null,
            airlines: (form.airlines || "")
              .split(",")
              .map((x) => x.trim().toUpperCase())
              .filter(Boolean),
            applicable_start_sector: to01(form.applicable_start_sector),
            applicable_end_sector: to01(form.applicable_end_sector),
            origin_display_name: originLabel,
            destination_display_name: destinationLabel,
          });
        } else {
          // UPI flight
          res = await api.post("/admin/promo-codes/preview-upi-wording", {
            promo_code_type: "UPI",
            promo_code_trip_type: "FLIGHT",
            max_booking_discount_amount: form.max_booking_discount_amount || 0,
            trip_type: form.trip_type,
            passenger_type: form.passenger_type,
            promo_wording_type: form.promo_wording_type,
            code: (form.code || "PREVIEW").toUpperCase(),
            min_booking_amount: form.min_booking_amount || 0,
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            travel_start_date: form.travel_start_date || null,
            travel_end_date: form.travel_end_date || null,
            to_destination: form.to_destination,
            sectors: form.sectors ? [String(form.sectors).trim().toUpperCase()] : null,
            airlines: (form.airlines || "")
              .split(",")
              .map((x) => x.trim().toUpperCase())
              .filter(Boolean),
            applicable_start_sector: to01(form.applicable_start_sector),
            applicable_end_sector: to01(form.applicable_end_sector),
            origin_display_name: originLabel,
            destination_display_name: destinationLabel,
          });
        }
        if (wordingRequestIdRef.current === requestId) {
          setAutoWording(res.data?.data || null);
        }
      } catch {
        if (wordingRequestIdRef.current === requestId) {
          setAutoWording(null);
        }
      } finally {
        if (wordingRequestIdRef.current === requestId) {
          setWordingPreviewLoading(false);
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [
    isWordingEligible,
    isCountryFlight,
    isNormalFlight,
    isNavCashFlight,
    form.promo_country_code,
    form.country_display_name,
    form.discount_type,
    form.discount_value,
    form.cashback_type,
    form.cashback_value,
    form.trip_type,
    form.passenger_type,
    form.promo_wording_type,
    form.code,
    form.min_booking_amount,
    form.max_booking_discount_amount,
    form.to_destination,
    form.sectors,
    form.airlines,
    form.applicable_start_sector,
    form.applicable_end_sector,
    form.start_date,
    form.end_date,
    form.travel_start_date,
    form.travel_end_date,
    destinationLabel,
    originLabel,
  ]);

  useEffect(() => {
    if (!isWordingEligible || !autoWording || wordingCustomized) return;
    setForm((s) => ({
      ...s,
      short_description: autoWording.short_description,
      description: autoWording.description,
      origin_display_name: originLabel,
      destination_display_name: destinationLabel,
    }));
  }, [autoWording, wordingCustomized, destinationLabel, originLabel, isWordingEligible]);

  const onChange = (k) => (e) =>
    setForm((s) => ({
      ...s,
      [k]: k === "code" ? (e.target.value || "").toUpperCase() : e.target.value,
    }));

  /** Rule fields that drive auto wording — reset custom flag so copy regenerates. */
  const onRuleFieldChange = (k) => (e) => {
    setWordingCustomized(false);
    onChange(k)(e);
  };

  const refreshWordingFromRules = () => setWordingCustomized(false);

  const parseAirlines = () =>
    (form.airlines || "")
      .split(",")
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean);

  const parseSectors = () => {
    if (isBus) {
      // Any-origin + Any-dest → no restriction (empty array)
      const originAny = to01(form.applicable_start_sector) === 1;
      const destAny   = to01(form.applicable_end_sector)   === 1;
      if (originAny && destAny) return [];
      if (!originAny && busOriginCity && !destAny && busDestCity) {
        return [{ from: busOriginCity, to: busDestCity }];
      }
      if (!originAny && busOriginCity) return [busOriginCity]; // origin-only list
      return null;
    }
    const fromCode = String(form.sectors || "").trim().toUpperCase();
    if (!fromCode) return null;
    return [fromCode];
  };

  const generateCode = async () => {
    try {
      setGenLoading(true);
      const res = await api.get("/admin/promo-codes/generate");
      const code = res.data?.data?.code;
      if (!code) throw new Error("Code missing from API");
      setForm((s) => ({ ...s, code: String(code).toUpperCase() }));
      toast.success("Promo code generated");
    } catch (e) {
      toast.error("Failed to generate code");
    } finally {
      setGenLoading(false);
    }
  };

  const uploadPdf = async (id) => {
    if (!pdfFile) return;
    const fd = new FormData();
    fd.append("pdf", pdfFile);
    setPdfUploading(true);
    try {
      await api.post(`/admin/promo-codes/${id}/pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error("PDF upload failed");
    } finally {
      setPdfUploading(false);
    }
  };

  const handleTripTypeTab = (tripType) => {
    setForm((s) => ({
      ...s,
      promo_code_trip_type: tripType,
      // Reset trip_type to ANY for bus (bus doesn't have ONE_WAY/RETURN concept)
      trip_type: tripType === "BUS" ? "ANY" : "ONE_WAY",
      bus_type: "ALL",
      min_passengers: "",
      max_passengers: "",
    }));
    setBusOriginCity("");
    setBusDestCity("");
    setSectorFromQuery("");
    setDestQuery("");
  };

  const doSave = async (editReason = "") => {
    if (saving || pdfUploading) return;

    const isPercent = form.discount_type === "PERCENT";

    {
      const errs = {};
      const minAmt = String(form.min_booking_amount ?? "").trim();

      if (isUpi) {
        // UPI validation — min and max always required (2.5% rate is hardcoded)
        if (!minAmt)
          errs.min_booking_amount = "Min Booking Amount is required";

        const maxAmt = String(form.max_booking_discount_amount ?? "").trim();
        if (!maxAmt || Number(maxAmt) <= 0)
          errs.max_booking_discount_amount = "Max UPI Discount Amount is required";
        // else if (minAmt && Number(maxAmt) > Number(minAmt) * 0.025) {
        //   errs.max_booking_discount_amount = `Max UPI Discount Amount cannot exceed 2.5% of Min Booking Amount (₹${(Number(minAmt) * 0.025).toFixed(2)})`;
        // }
      } else if (!isNavCash) {
        // Normal / Targeted promo validation
        if (!minAmt)
          errs.min_booking_amount = "Min Booking Amount is required";

        if (isPercent) {
          const maxAmt = String(form.max_booking_discount_amount ?? "").trim();
          if (!maxAmt || Number(maxAmt) <= 0)
            errs.max_booking_discount_amount = "Max Booking Discount Amount is required for PERCENT discount";
        }
      } else {
        // NavCash validation — min always required; max required for PERCENT cashback
        if (!minAmt)
          errs.min_booking_amount = "Min Booking Amount is required";

        if (form.cashback_type === "PERCENT") {
          const maxAmt = String(form.max_booking_discount_amount ?? "").trim();
          if (!maxAmt || Number(maxAmt) <= 0)
            errs.max_booking_discount_amount = "Max NavCash Amount is required for PERCENT cashback";
        }
      }

      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        toast.error(Object.values(errs)[0]);
        return;
      }
    }
    setErrors({});

    const startAny = to01(form.applicable_start_sector);
    const endAny = to01(form.applicable_end_sector);

    // A promo uses either sectors/to_destination or promo_country_code, never both.
    const sectorsPayload = hasCountryLock ? null : parseSectors();
    const toDestPayload = hasCountryLock
      ? null
      : isBus
      ? (endAny === 0 && busDestCity ? busDestCity : null)
      : (endAny === 1 ? null : String(form.to_destination || "").trim());

    const payload = {
      promo_code_trip_type: form.promo_code_trip_type,
      promo_code_type: form.promo_code_type,
      promo_wording_type: (isWordingEligible || isCountryFlight) ? form.promo_wording_type : undefined,
      origin_display_name: isWordingEligible ? originLabel : undefined,
      destination_display_name: isWordingEligible ? destinationLabel : undefined,
      promo_country_code: hasCountryLock ? form.promo_country_code : null,
      country_display_name: hasCountryLock ? form.country_display_name : null,
      wording_edit_reason: editReason || undefined,
      code: form.code.trim().toUpperCase(),
      // NavCash/UPI promos don't use discount fields — send DB-safe defaults
      discount_type: (isNavCash || isUpi) ? "FLAT" : form.discount_type,
      discount_value: (isNavCash || isUpi) ? 0 : Number(form.discount_value),
      cashback_type: isNavCash ? form.cashback_type : null,
      cashback_value: isNavCash && form.cashback_value !== "" ? Number(form.cashback_value) : null,
      trip_type: form.trip_type,
      passenger_type: form.passenger_type,
      max_usage_per_user: Number(form.max_usage_per_user) || 1,
      max_usage_per_device: Number(form.max_usage_per_device) || 1,
      airlines: isBus ? [] : parseAirlines(),
      sectors: sectorsPayload,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      travel_start_date: form.travel_start_date ? new Date(form.travel_start_date).toISOString() : null,
      travel_end_date: form.travel_end_date ? new Date(form.travel_end_date).toISOString() : null,
      to_destination: toDestPayload,
      applicable_start_sector: startAny,
      applicable_end_sector: endAny,
      min_booking_amount: form.min_booking_amount === "" ? null : Number(form.min_booking_amount),
      description: (form.description || "").trim() || null,
      short_description: (form.short_description || "").trim() || null,
      max_booking_discount_amount:
        isUpi ||
        (form.promo_code_type !== "NAVCASH" && form.discount_type === "PERCENT") ||
        (form.promo_code_type === "NAVCASH" && form.cashback_type === "PERCENT")
          ? Number(form.max_booking_discount_amount) || null
          : null,
      // Bus-specific
      bus_type: isBus ? (form.bus_type || "ALL") : null,
      min_passengers: isBus && form.min_passengers !== "" ? Number(form.min_passengers) : null,
      max_passengers: isBus && form.max_passengers !== "" ? Number(form.max_passengers) : null,
    };

    if (isEdit) payload.status = Number(form.status);

    setSaving(true);
    try {
      let createdId = promoId;

      if (!isEdit) {
        const res = await api.post("/admin/promo-codes", payload);
        createdId = res?.data?.promoCodeId || res?.data?.data?.promo_id || res?.data?.data?.id;
        toast.success("Promo created");
      } else {
        await api.patch(`/admin/promo-codes/${promoId}`, payload);
        toast.success("Promo updated");
      }

      if (pdfFile && createdId) await uploadPdf(createdId);
      navigate("/admin/promo-codes");
    } catch (e2) {
      toast.error(e2?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
      setShowEditReasonModal(false);
      setWordingEditReason("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || pdfUploading) return;

    if (
      isWordingEligible &&
      wordingCustomized &&
      autoWording &&
      !wordingTextsMatch(
        { short_description: form.short_description, description: form.description },
        autoWording
      ) &&
      !String(wordingEditReason || "").trim()
    ) {
      setShowEditReasonModal(true);
      return;
    }

    await doSave(wordingEditReason);
  };

  const confirmEditReasonAndSave = async () => {
    if (!String(wordingEditReason || "").trim()) {
      toast.error("Please provide a reason for the wording change");
      return;
    }
    await doSave(wordingEditReason);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold">
          {isEdit ? "Edit Promo" : "Create Promo"}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/admin/promo-codes")}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start sm:self-auto"
        >
          Back
        </button>
      </div>

      {/* ── Trip Type Tabs ─────────────────────────────── */}
      <div className="flex justify-center mb-6 overflow-x-auto">
      <div className="flex gap-0 border rounded-xl overflow-hidden w-fit">
        {["FLIGHT", "BUS"].map((type) => (
          <button
            key={type}
            type="button"
            disabled={isEdit}
            onClick={() => handleTripTypeTab(type)}
            className={`px-4 sm:px-8 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors disabled:cursor-not-allowed ${
              form.promo_code_trip_type === type
                ? "bg-black text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {type === "FLIGHT" ? "✈ Flight" : "🚌 Bus"}
          </button>
        ))}
      </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Promo Code Type (NORMAL / NAVCASH / UPI) */}
          <div>
            <label className="text-sm font-semibold">Promo Code Type</label>
            <div className="mt-2 flex flex-wrap gap-3">
              {["NORMAL", "NAVCASH", "UPI"].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setForm((s) => ({ ...s, promo_code_type: type }))}
                  className={`flex-1 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${
                    form.promo_code_type === type
                      ? "bg-black text-white border-black"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {isWordingEligible && (
            <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50/80 to-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div>
                  <label className="text-sm font-semibold text-gray-900">
                    Promo Wording Type
                  </label>
                  <p className="text-xs text-gray-500 mt-1 max-w-xl">
                    Pick the tone for auto-generated short description and terms. You can still edit text below; edits require an audit reason on save.
                  </p>
                </div>
                {wordingPreviewLoading ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 shrink-0">
                    <RefreshCcw size={12} className="animate-spin" aria-hidden />
                    Updating copy…
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  {
                    value: "NORMAL",
                    title: "Normal",
                    Icon: Type,
                    hint: "Straightforward savings copy (e.g. “One-way to …? Save up to …”).",
                  },
                  {
                    value: "HOLIDAY",
                    title: "Holiday",
                    Icon: Sparkles,
                    hint: "Seasonal, upbeat tone (e.g. “Fly to … with up to … off!”).",
                  },
                ].map(({ value, title, Icon, hint }) => {
                  const selected = form.promo_wording_type === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setWordingCustomized(false);
                        setForm((s) => ({ ...s, promo_wording_type: value }));
                      }}
                      className={`flex w-full text-left rounded-xl border-2 p-3.5 transition-all outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 ${
                        selected
                          ? "border-black bg-black text-white shadow-md"
                          : "border-gray-200 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50/80"
                      }`}
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                          selected ? "bg-white/15 text-white" : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        <Icon size={20} strokeWidth={2} aria-hidden />
                      </span>
                      <span className="ml-3 min-w-0 flex-1">
                        <span className="block text-sm font-bold tracking-tight">
                          {title}
                        </span>
                        <span
                          className={`mt-1 block text-xs leading-snug ${
                            selected ? "text-white/85" : "text-gray-500"
                          }`}
                        >
                          {hint}
                        </span>
                      </span>
                      {selected ? (
                        <span className="ml-2 shrink-0 self-start rounded-md bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                          Active
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {wordingCustomized && autoWording ? (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2">
                  <p className="text-xs text-amber-900">
                    Wording was edited manually from the auto-generated version.
                  </p>
                  <button
                    type="button"
                    className="text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
                    onClick={() => {
                      setForm((s) => ({
                        ...s,
                        short_description: autoWording.short_description,
                        description: autoWording.description,
                        origin_display_name: originLabel,
                        destination_display_name: destinationLabel,
                      }));
                      setWordingCustomized(false);
                    }}
                  >
                    Reset to auto wording
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {/* Code */}
          <div>
            <label className="text-sm font-semibold">Code</label>
            <div className="relative mt-2">
              <input
                className="w-full border rounded-xl px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-black disabled:bg-gray-50"
                value={form.code}
                onChange={onChange("code")}
                disabled={isEdit}
              />
              {!isEdit && (
                <button
                  type="button"
                  onClick={generateCode}
                  disabled={genLoading || saving || pdfUploading}
                  title="Generate promo code"
                  className="absolute inset-y-0 right-2 flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-50"
                >
                  <RefreshCcw size={18} className={genLoading ? "animate-spin" : ""} />
                </button>
              )}
            </div>
          </div>

          {/* Short Description */}
          <div>
            <label className="text-sm font-semibold">Short Description</label>
            <input
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={form.short_description}
              onChange={(e) => {
                setWordingCustomized(true);
                onChange("short_description")(e);
              }}
              placeholder="Shown in preview"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-semibold">Description (Terms &amp; Conditions)</label>
            <textarea
              className="mt-2 w-full border rounded-xl px-3 py-2 min-h-[160px] resize-none"
              value={form.description}
              onChange={(e) => {
                setWordingCustomized(true);
                onChange("description")(e);
              }}
              placeholder="Write full promo description..."
            />
          </div>

          {/* UPI info banner — 2.5% discount rate is hardcoded, never configurable */}
          {/* {isUpi && (
            <div className="p-4 border rounded-xl bg-emerald-50">
              <p className="text-sm font-semibold text-emerald-700">UPI Discount</p>
              <p className="text-xs text-emerald-600 mt-1">
                Discount rate is fixed at 2.5% of the total fare and is applied to the
                convenience fee at checkout. Only Min Booking Amount and Max UPI Discount
                Amount below are configurable. UPI is the only payment method accepted
                when this promo is applied.
              </p>
            </div>
          )} */}

          {/* Discount */}
          {!isNavCash && !isUpi && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Discount Type</label>
              <select
                className="mt-2 w-full border rounded-xl px-3 py-2"
                value={form.discount_type}
                onChange={(e) => {
                  setWordingCustomized(false);
                  setForm((s) => ({
                    ...s,
                    discount_type: e.target.value,
                    max_booking_discount_amount:
                      e.target.value === "PERCENT" ? s.max_booking_discount_amount : "",
                  }));
                  setErrors({});
                }}
              >
                <option value="PERCENT">PERCENT</option>
                <option value="FLAT">FLAT</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Discount Value</label>
              <input
                type="number"
                min={1}
                max={form.discount_type === "PERCENT" ? 50 : undefined}
                className="mt-2 w-full border rounded-xl px-3 py-2"
                value={form.discount_value}
                onChange={(e) => {
                  const raw = e.target.value;
                  const isPercent = form.discount_type === "PERCENT";
                  if (raw === "" || !isPercent || Number(raw) <= 50) {
                    onRuleFieldChange("discount_value")(e);
                  } else {
                    setForm((s) => ({ ...s, discount_value: 50 }));
                    setWordingCustomized(false);
                    toast.error("Discount Value cannot exceed 50%");
                  }
                }}
              />
              {form.discount_type === "PERCENT" && form.discount_value !== "" && Number(form.discount_value) > 50 && (
                <p className="text-red-500 text-xs mt-1">Maximum allowed value is 50%</p>
              )}
            </div>
          </div>
          )}

          {/* NavCash cashback */}
          {form.promo_code_type === "NAVCASH" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 border rounded-xl bg-blue-50">
              <div className="col-span-2">
                <p className="text-sm font-semibold text-blue-700">
                  NavCash Cashback
                  {isBus && (
                    <span className="ml-2 text-xs font-normal text-blue-500">
                      (Max ₹200 per journey)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold">Cashback Type</label>
                <select
                  className="mt-2 w-full border rounded-xl px-3 py-2 bg-white"
                  value={form.cashback_type}
                  onChange={onChange("cashback_type")}
                >
                  <option value="PERCENT">PERCENT</option>
                  <option value="FLAT">FLAT</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold">Cashback Value</label>
                <input
                  type="number"
                  min={0}
                  max={form.cashback_type === "PERCENT" ? 50 : undefined}
                  className="mt-2 w-full border rounded-xl px-3 py-2 bg-white"
                  value={form.cashback_value}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const isPercent = form.cashback_type === "PERCENT";
                    if (raw === "" || !isPercent || Number(raw) <= 50) {
                      onChange("cashback_value")(e);
                    } else {
                      setForm((s) => ({ ...s, cashback_value: 50 }));
                      toast.error("Cashback Value cannot exceed 50%");
                    }
                  }}
                />
                {form.cashback_type === "PERCENT" && form.cashback_value !== "" && Number(form.cashback_value) > 50 && (
                  <p className="text-red-500 text-xs mt-1">Maximum allowed value is 50%</p>
                )}
              </div>
            </div>
          )}

          {(() => {
            const isNavCashPercent = isNavCash && form.cashback_type === "PERCENT";
            const isNormalPercent = !isNavCash && !isUpi && form.discount_type === "PERCENT";
            const isMaxEnabled = isNormalPercent || isNavCashPercent || isUpi;
            const maxLabel = isUpi
              ? "Max UPI Discount Amount (₹)"
              : isNavCashPercent
              ? "Max NavCash Amount"
              : "Max Booking Discount Amount (Percent only)";
            return (
              <div>
                <label className="text-sm font-semibold">
                  {maxLabel}
                  {isMaxEnabled && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="number"
                  min={1}
                  disabled={!isMaxEnabled}
                  className={`mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50 ${errors.max_booking_discount_amount ? "border-red-500" : ""}`}
                  value={form.max_booking_discount_amount}
                  onChange={(e) => {
                    onRuleFieldChange("max_booking_discount_amount")(e);
                    setErrors((p) => ({ ...p, max_booking_discount_amount: undefined }));
                  }}
                />
                {errors.max_booking_discount_amount && (
                  <p className="text-red-500 text-xs mt-1">{errors.max_booking_discount_amount}</p>
                )}
              </div>
            );
          })()}

          <div>
            <label className="text-sm font-semibold">Max Usage/User</label>
            <input
              type="number" min={1}
              className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50"
              value={form.max_usage_per_user}
              onChange={onChange("max_usage_per_user")}
              disabled={isEdit}
            />
          </div>

          <div>
            <label className="text-sm font-semibold">Max Usage/Device</label>
            <input
              type="number" min={1}
              className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50"
              value={form.max_usage_per_device}
              onChange={onChange("max_usage_per_device")}
              disabled={isEdit}
            />
          </div>

          <div>
            <label className="text-sm font-semibold">
              Min Booking Amount
              {form.promo_code_type !== "NAVCASH" && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              min={0}
              className={`mt-2 w-full border rounded-xl px-3 py-2 ${errors.min_booking_amount ? "border-red-500" : ""}`}
              value={form.min_booking_amount}
              onChange={(e) => {
                onRuleFieldChange("min_booking_amount")(e);
                setErrors((p) => ({ ...p, min_booking_amount: undefined }));
              }}
            />
            {errors.min_booking_amount && (
              <p className="text-red-500 text-xs mt-1">{errors.min_booking_amount}</p>
            )}
          </div>

          {/* ── Bus-specific fields ─────────────────────── */}
          {isBus && (
            <div className="p-4 border rounded-xl bg-amber-50 space-y-4">
              {/* <p className="text-sm font-semibold text-amber-700">Bus Settings</p> */}

              <div>
                <label className="text-sm font-semibold">Bus Type</label>
                <select
                  className="mt-2 w-full border rounded-xl px-3 py-2 bg-white"
                  value={form.bus_type}
                  onChange={onChange("bus_type")}
                >
                  <option value="ALL">All</option>
                  <option value="AC">AC</option>
                  <option value="NON_AC">Non-AC</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold">Min Passengers</label>
                  <input
                    type="number" min={1}
                    className="mt-2 w-full border rounded-xl px-3 py-2 bg-white"
                    value={form.min_passengers}
                    onChange={onChange("min_passengers")}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">Max Passengers</label>
                  <input
                    type="number" min={1}
                    className="mt-2 w-full border rounded-xl px-3 py-2 bg-white"
                    value={form.max_passengers}
                    onChange={onChange("max_passengers")}
                    placeholder="Optional"
                  />
                </div>
              </div>

              {/* Bus Route — separate Any checkboxes for origin and destination */}
              <div>
                <label className="text-sm font-semibold">Route</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  {/* From city */}
                  <div className="relative">
                    <label className="text-xs font-medium flex items-center justify-between">
                      From city
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={to01(form.applicable_start_sector) === 1}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setForm((s) => ({ ...s, applicable_start_sector: checked ? 1 : 0 }));
                            if (checked) setBusOriginCity("");
                          }}
                        />
                        Any
                      </span>
                    </label>
                    <div className="mt-2">
                      <BusCitySearch
                        label=""
                        value={busOriginCity}
                        onSelect={(name) => setBusOriginCity(name)}
                        disabled={to01(form.applicable_start_sector) === 1}
                      />
                    </div>
                  </div>

                  {/* To city */}
                  <div className="relative">
                    <label className="text-xs font-medium flex items-center justify-between">
                      To city
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={to01(form.applicable_end_sector) === 1}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setForm((s) => ({ ...s, applicable_end_sector: checked ? 1 : 0 }));
                            if (checked) setBusDestCity("");
                          }}
                        />
                        Any
                      </span>
                    </label>
                    <div className="mt-2">
                      <BusCitySearch
                        label=""
                        value={busDestCity}
                        onSelect={(name) => setBusDestCity(name)}
                        disabled={to01(form.applicable_end_sector) === 1}
                      />
                    </div>
                  </div>
                </div>

                {(busOriginCity || busDestCity) && (
                  <p className="text-xs text-gray-500 mt-2">
                    Route: {to01(form.applicable_start_sector) === 1 ? "Any" : (busOriginCity || "—")}
                    {" → "}
                    {to01(form.applicable_end_sector) === 1 ? "Any" : (busDestCity || "—")}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Flight-specific fields ──────────────────── */}
          {!isBus && (
            <>
              <div>
                <label className="text-sm font-semibold">Sectors</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <div className="relative">
                    <label className="text-xs font-medium flex items-center justify-between">
                      From sector
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={to01(form.applicable_start_sector) === 1}
                          onChange={(e) => {
                            refreshWordingFromRules();
                            const checked = e.target.checked;
                            setForm((s) => ({
                              ...s,
                              applicable_start_sector: checked ? 1 : 0,
                              sectors: checked ? "" : s.sectors,
                            }));
                            if (checked) {
                              setSectorFromQuery("");
                              setSectorFromOpen(false);
                            }
                          }}
                        />
                        Any
                      </span>
                    </label>

                    <input
                      disabled={to01(form.applicable_start_sector) === 1 || hasCountryLock}
                      className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
                      value={sectorFromQuery}
                      placeholder="Type code or choose..."
                      onFocus={() => setSectorFromOpen(true)}
                      onBlur={() => {
                        setTimeout(() => setSectorFromOpen(false), 150);
                        const code = extractAirportCode(sectorFromQuery);
                        refreshWordingFromRules();
                        setForm((s) => ({ ...s, sectors: code }));
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSectorFromQuery(val);
                        setSectorFromOpen(true);
                        const code = extractAirportCode(val);
                        refreshWordingFromRules();
                        setForm((s) => ({ ...s, sectors: code }));
                      }}
                    />

                    {sectorFromOpen && sectorFromResults.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
                        {sectorFromResults.map((a) => (
                          <button
                            key={a.airport_code}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              refreshWordingFromRules();
                              setSectorFromQuery(`${a.airport_name} (${a.airport_code})`);
                              setForm((s) => ({ ...s, sectors: String(a.airport_code) }));
                              setSectorFromOpen(false);
                            }}
                          >
                            {a.airport_name} ({a.airport_code})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <label className="text-xs font-medium flex items-center justify-between">
                      To destination
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={to01(form.applicable_end_sector) === 1}
                          onChange={(e) => {
                            refreshWordingFromRules();
                            const checked = e.target.checked;
                            setForm((s) => ({
                              ...s,
                              applicable_end_sector: checked ? 1 : 0,
                              to_destination: checked ? "" : s.to_destination,
                            }));
                            if (checked) { setDestQuery(""); setDestOpen(false); }
                          }}
                        />
                        Any
                      </span>
                    </label>

                    <input
                      disabled={to01(form.applicable_end_sector) === 1 || hasCountryLock}
                      className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black disabled:bg-gray-50 disabled:cursor-not-allowed"
                      value={destQuery}
                      placeholder="Type code or choose..."
                      onFocus={() => { if (to01(form.applicable_end_sector) === 0 && !hasCountryLock) setDestOpen(true); }}
                      onBlur={() => {
                        setTimeout(() => setDestOpen(false), 150);
                        const code = extractAirportCode(destQuery);
                        refreshWordingFromRules();
                        setForm((s) => ({ ...s, to_destination: code }));
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setDestQuery(val);
                        setDestOpen(true);
                        const code = extractAirportCode(val);
                        refreshWordingFromRules();
                        setForm((s) => ({ ...s, to_destination: code }));
                      }}
                    />

                    {destOpen && destResults.length > 0 && to01(form.applicable_end_sector) === 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
                        {destResults.map((a) => (
                          <button
                            key={a.airport_code}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              refreshWordingFromRules();
                              const toCode = String(a.airport_code);
                              setDestQuery(`${a.airport_name} (${toCode})`);
                              setForm((s) => ({ ...s, to_destination: toCode }));
                              setDestOpen(false);
                            }}
                          >
                            {a.airport_name} ({a.airport_code})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {form.sectors ? (
                  <p className="text-xs text-gray-500 mt-2">Stored sectors: {form.sectors}</p>
                ) : null}
              </div>

              <div>
                <label className="text-sm font-semibold">Country Lock</label>
                {/* <p className="text-xs text-gray-500 mt-1">
                  Valid for every airport in one country, only when the journey crosses that
                  country's border. Uses either this or Sectors/To destination above, not both.
                </p> */}
                <div className="mt-2">
                  <CountrySearch
                    code={form.promo_country_code}
                    displayName={form.country_display_name}
                    disabled={Boolean(form.sectors) || Boolean(form.to_destination)}
                    onSelect={(code, name) => {
                      refreshWordingFromRules();
                      setForm((s) => ({
                        ...s,
                        promo_country_code: code,
                        country_display_name: name,
                        sectors: "",
                        to_destination: "",
                        applicable_start_sector: 0,
                        applicable_end_sector: 0,
                      }));
                      setSectorFromQuery("");
                      setDestQuery("");
                    }}
                    onClear={() => {
                      refreshWordingFromRules();
                      setForm((s) => ({
                        ...s,
                        promo_country_code: "",
                        country_display_name: "",
                      }));
                    }}
                  />
                </div>
              </div>

              <AirlineTagInput
                value={form.airlines}
                onChange={(v) => {
                  refreshWordingFromRules();
                  setForm((s) => ({ ...s, airlines: v }));
                }}
              />
            </>
          )}

          {/* Trip Type */}
          <div>
            <label className="text-sm font-semibold">Trip Type</label>
            <select
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={form.trip_type}
              onChange={onRuleFieldChange("trip_type")}
            >
              {isBus ? (
                <option value="ANY">ANY (General)</option>
              ) : (
                <>
                  <option value="ONE_WAY">ONE WAY</option>
                  <option value="RETURN">RETURN</option>
                  <option value="ANY">ANY</option>
                </>
              )}
            </select>
          </div>

          {/* Passenger Type */}
          <div>
            <label className="text-sm font-semibold">Passenger Type</label>
            <select
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={form.passenger_type}
              onChange={onRuleFieldChange("passenger_type")}
            >
              <option value="SINGLE">SINGLE</option>
              <option value="MULTIPLE">MULTIPLE</option>
              <option value="ANY">ANY</option>
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Start Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.start_date} onChange={onRuleFieldChange("start_date")} />
            </div>
            <div>
              <label className="text-sm font-semibold">End Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.end_date} onChange={onRuleFieldChange("end_date")} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Travel Start Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.travel_start_date} onChange={onRuleFieldChange("travel_start_date")} />
            </div>
            <div>
              <label className="text-sm font-semibold">Travel End Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.travel_end_date} onChange={onRuleFieldChange("travel_end_date")} />
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="text-sm font-semibold">Status</label>
              <select className="mt-2 w-full border rounded-xl px-3 py-2" value={form.status} onChange={onChange("status")}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold">Promo PDF (optional)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              className="mt-2 w-full"
            />
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate("/admin/promo-codes")}
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              disabled={saving || pdfUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || pdfUploading}
              className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving || pdfUploading ? "Saving..." : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>

        <div>
          <h2 className="text-lg font-bold mb-3">Preview</h2>
          <PromoPreviewCard form={form} />
        </div>
      </div>

      {showEditReasonModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-lg">
            <h3 className="text-lg font-bold">Reason for wording change</h3>
            <p className="text-sm text-gray-600 mt-2">
              Edits to auto-generated promo wording require an audit reason.
            </p>
            <textarea
              className="mt-4 w-full border rounded-xl px-3 py-2 min-h-[100px]"
              value={wordingEditReason}
              onChange={(e) => setWordingEditReason(e.target.value)}
              placeholder="Describe why the wording was changed..."
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="rounded-xl border px-4 py-2 text-sm font-semibold"
                onClick={() => setShowEditReasonModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold"
                onClick={confirmEditReasonAndSave}
                disabled={saving}
              >
                Save with reason
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
